import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore, selectPermissions } from '@/app/store/authStore';
import { paths } from '@/app/routes/routePaths';
import type { Permission } from '@/config/constants';
import { useShallow } from 'zustand/react/shallow';

interface RoleGuardProps {
  requiredPermissions: Permission[];
  requireAll?: boolean; // default: ANY of the permissions is sufficient
  children?: React.ReactNode;
}

// Layered on top of ProtectedRoute for routes that need a specific
// permission, e.g. /admin/users needs `users.view`. Kept separate from
// ProtectedRoute so "logged in" and "authorized" stay independently
// testable and composable in the route tree.
export function RoleGuard({ requiredPermissions, requireAll = false, children }: RoleGuardProps) {
  const permissions = useAuthStore(useShallow(selectPermissions));
  const isSuperAdmin = useAuthStore(s => s.user?.roles.some(r => r.name === 'super-admin' || r.name === 'developer' || r.name === 'Admin'));

  const isAuthorized = isSuperAdmin || (requireAll
    ? requiredPermissions.every((p) => permissions.includes(p as any))
    : requiredPermissions.some((p) => permissions.includes(p as any)));

  if (!isAuthorized) {
    return <Navigate to={paths.forbidden} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
